import React, { useState, useMemo } from 'react';
import './db_stats.css';
// # Import Recharts components for visualization
import { 
  BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer 
} from 'recharts';

const DbStats = ({ rows }) => {
  // # State for collapse/expand
  const [isExpanded, setIsExpanded] = useState(true);
  // # Add state for chart view toggle
  const [showCharts, setShowCharts] = useState(true);

  // # Calculate statistics
  const stats = useMemo(() => {
    const totalTasks = rows.length;
    
    // # Group tasks by plan
    const tasksByPlan = rows.reduce((acc, row) => {
      const plan = row.plan || 'Unassigned';
      acc[plan] = (acc[plan] || 0) + 1;
      return acc;
    }, {});

    // # Group tasks by source
    const tasksBySource = rows.reduce((acc, row) => {
      const source = row.source || 'Unassigned';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {});

    // # Calculate total hours
    const totalMinutes = rows.reduce((acc, row) => {
      return acc + (parseInt(row.duration) || 0);
    }, 0);
    const totalHours = (totalMinutes / 60).toFixed(1);

    // # Calculate hours by plan
    const hoursByPlan = rows.reduce((acc, row) => {
      const plan = row.plan || 'Unassigned';
      const duration = parseInt(row.duration) || 0;
      acc[plan] = Number(acc[plan] || 0) + duration / 60;
      return acc;
    }, {});

    // Format hours to 1 decimal place after calculations
    Object.keys(hoursByPlan).forEach(key => {
      hoursByPlan[key] = hoursByPlan[key].toFixed(1);
    });

    // # Prepare data for charts
    const planChartData = Object.entries(tasksByPlan).map(([name, value]) => ({
      name,
      value
    }));

    const sourceChartData = Object.entries(tasksBySource).map(([name, value]) => ({
      name,
      value
    }));

    const hoursByPlanChartData = Object.entries(hoursByPlan).map(([name, value]) => ({
      name,
      value: parseFloat(value)
    }));

    return {
      totalTasks,
      tasksByPlan,
      tasksBySource,
      totalHours,
      hoursByPlan,
      // # Add chart data
      planChartData,
      sourceChartData,
      hoursByPlanChartData
    };
  }, [rows]);

  // # Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];

  return (
    <div className="mstr_db_stats_container">
      {/* # Stats Header */}
      <div 
        className="mstr_db_stats_header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2>
          <i className="fas fa-chart-bar"></i>
          Task Statistics
        </h2>
        <div className="mstr_db_stats_header_controls">
          {isExpanded && (
            <button 
              className="mstr_db_stats_view_toggle" 
              onClick={(e) => {
                e.stopPropagation();
                setShowCharts(!showCharts);
              }}
            >
              {showCharts ? 'Show Text View' : 'Show Charts'}
            </button>
          )}
          <i className={`fas fa-chevron-down mstr_db_stats_collapse_icon ${isExpanded ? 'expanded' : ''}`}></i>
        </div>
      </div>

      {/* # Stats Content */}
      <div className={`mstr_db_stats_content ${isExpanded ? 'expanded' : ''}`}>
        {/* # Overview Section */}
        <div className="mstr_db_stats_section">
          <div className="mstr_db_stats_total">
            Total Tasks: {stats.totalTasks}
          </div>
          <div className="mstr_db_stats_total">
            Total Hours: {stats.totalHours}
          </div>
        </div>

        {/* # Tasks by Plan */}
        <div className="mstr_db_stats_section">
          <h3>Tasks by Plan</h3>
          {showCharts ? (
            <div className="mstr_db_stats_chart">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.planChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3498db" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ul className="mstr_db_stats_list">
              {Object.entries(stats.tasksByPlan).map(([plan, count]) => (
                <li key={plan}>
                  <span>{plan}</span>
                  <span className="mstr_db_stats_value">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* # Tasks by Source */}
        <div className="mstr_db_stats_section">
          <h3>Tasks by Source</h3>
          {showCharts ? (
            <div className="mstr_db_stats_chart">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={stats.sourceChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {stats.sourceChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} tasks`, 'Count']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ul className="mstr_db_stats_list">
              {Object.entries(stats.tasksBySource).map(([source, count]) => (
                <li key={source}>
                  <span>{source}</span>
                  <span className="mstr_db_stats_value">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* # Hours by Plan */}
        <div className="mstr_db_stats_section">
          <h3>Hours by Plan</h3>
          {showCharts ? (
            <div className="mstr_db_stats_chart">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.hoursByPlanChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} hours`, 'Duration']} />
                  <Bar dataKey="value" fill="#27ae60" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ul className="mstr_db_stats_list">
              {Object.entries(stats.hoursByPlan).map(([plan, hours]) => (
                <li key={plan}>
                  <span>{plan}</span>
                  <span className="mstr_db_stats_value">{hours}h</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DbStats;
